import { useCallback, useContext } from 'react';
import type { RemirrorJSON } from '@remirror/core';
import { InputControlsContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { EditorDocContext } from '../context';
import { useChatTextInputControls } from './useChatTextInputControls';

type SetChatInputTextMode = 'replace' | 'append';

export type SetChatInputTextOptions = {
  focus?: boolean;
  mode?: SetChatInputTextMode;
  promptHintIdentifier?: string | null;
  richDoc?: RemirrorJSON;
};

export type SetChatInputText = (value: string, options?: SetChatInputTextOptions) => void;

export function useSetChatInputText(): SetChatInputText {
  const dispatch = useContext(ChatStateDispatchContext);
  const inputControls = useContext(InputControlsContext);
  const editorDocRef = useContext(EditorDocContext);
  const { focusChatInputTextArea } = useChatTextInputControls();

  const dispatchInputChanged = useCallback(
    (value: string, promptHintIdentifier?: string | null) => {
      if (!dispatch) {
        return;
      }

      dispatch({
        type: 'input-changed',
        payload:
          promptHintIdentifier != null
            ? { value, promptHintIdentifier }
            : {
                value,
              },
      });
    },
    [dispatch]
  );

  const handleAppendMode = useCallback(
    (value: string) => {
      if (inputControls?.insertTextAtCursor) {
        inputControls.insertTextAtCursor(value);
        return;
      }

      if (dispatch) {
        dispatch({
          type: 'input-appended',
          payload: { value },
        });
        return;
      }
    },
    [dispatch, inputControls]
  );

  const handleReplaceMode = useCallback(
    (value: string, options: Pick<SetChatInputTextOptions, 'promptHintIdentifier' | 'richDoc'>) => {
      if (inputControls?.replaceInput) {
        if (options.richDoc && inputControls.replaceInputDoc) {
          inputControls.replaceInputDoc(options.richDoc);
          editorDocRef.current = options.richDoc;
        } else {
          inputControls.replaceInput(value);
        }

        if (options.promptHintIdentifier != null) {
          dispatchInputChanged(value, options.promptHintIdentifier);
        }

        return;
      }

      if (dispatch) {
        dispatchInputChanged(value, options.promptHintIdentifier);
        return;
      }
    },
    [dispatch, dispatchInputChanged, editorDocRef, inputControls]
  );

  return useCallback(
    (value: string, options?: SetChatInputTextOptions) => {
      const { focus = false, mode = 'replace', promptHintIdentifier, richDoc } = options ?? {};

      if (mode === 'append') {
        handleAppendMode(value);
      } else {
        handleReplaceMode(value, { promptHintIdentifier, richDoc });
      }

      if (focus) {
        focusChatInputTextArea();
      }
    },
    [focusChatInputTextArea, handleAppendMode, handleReplaceMode]
  );
}
